
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import { e as capitalize } from './labels-mixin.js';
import 'focus-visible/dist/focus-visible.js';
import { _ as _objectSpread } from './_rollupPluginBabelHelpers-mixin.js';
import 'lodash/kebabCase';
import 'feather-icons';
import './pendo-icon.js';
import Vue from 'vue';
import 'lodash/get';
import './pendo-loading-mixin.js';
import './pendo-loading-indicator.js';
import PendoButton from './pendo-button.js';
import 'lodash/isNumber';
import { c as getTarget, s as setStyles, d as getMargin } from './table-auto-height-mixin.js';

var PortalTarget = Vue.extend({
  abstract: true,
  name: 'PortalTarget',
  props: ['nodes'],
  data: vm => ({
    updatedNodes: vm.nodes
  }),

  destroyed() {
    const el = this.$el;
    el.parentNode.removeChild(el);
  },

  render(createElement) {
    const nodes = this.updatedNodes && this.updatedNodes();

    if (!nodes) {
      return createElement();
    }

    if (nodes.length < 2 && !nodes[0].text) {
      return nodes;
    }

    return createElement('div', nodes);
  }

});

var Portal = Vue.extend({
  name: 'Portal',
  props: {
    disabled: {
      type: Boolean
    },
    appendTo: {
      type: [String, Element, Boolean],
      default: 'body'
    }
  },
  watch: {
    disabled: {
      immediate: true,

      handler(disabled) {
        if (disabled) {
          this.unmount();
        } else {
          this.$nextTick(this.mount);
        }
      }

    }
  },

  async updated() {
    await this.$nextTick();

    if (!this.disabled && this.slotFn !== this.$scopedSlots.default) {
      this.container.updatedNodes = this.$scopedSlots.default;
    }

    this.slotFn = this.$scopedSlots.default;
  },

  beforeDestroy() {
    this.unmount();
  },

  methods: {
    mount() {
      const targetEl = getTarget(this.appendTo);

      if (!targetEl) {
        console.warn(`[pendo-portal]: could not locate portal target ${targetEl}`);
        return;
      }

      const el = document.createElement('div');
      targetEl.appendChild(el);
      this.container = new PortalTarget({
        el,
        parent: this,
        propsData: {
          nodes: this.$scopedSlots.default
        }
      });
    },

    unmount() {
      if (this.container) {
        this.container.$destroy();
        delete this.container;
      }
    }

  },

  render(createElement) {
    if (this.disabled) {
      const nodes = this.$scopedSlots && this.$scopedSlots.default();

      if (!nodes) {
        return createElement();
      }

      if (nodes.length < 2 && !nodes[0].text) {
        return nodes;
      }

      return createElement('div', nodes);
    }

    return createElement();
  }

});

const SCROLL_BLOCK_CLASS = 'pendo-components--is-blocking-scroll';
var script = {
  name: 'PendoDrawer',
  components: {
    PendoButton,
    Portal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    className: {
      type: [String, Array],
      default: null
    },
    size: {
      type: String,
      default: 'medium'
    },
    title: {
      type: String,
      default: null
    },
    closeOnMaskClick: {
      type: Boolean,
      default: true
    },
    mask: {
      type: Boolean,
      default: true
    },
    placement: {
      type: String,

      default() {
        if (this.size === 'fullscreen') {
          return 'bottom';
        }

        return 'right';
      },

      validator: placement => ['top', 'bottom', 'right', 'left'].includes(placement)
    },
    zIndex: {
      type: Number,
      default: 502
    },
    push: {
      type: [String, Element, Boolean],
      default: null
    },
    duration: {
      type: Number,
      default: 220
    },
    appendTo: {
      type: [String, Element, Boolean],
      default: 'body'
    }
  },

  data() {
    return {
      isPanelVisible: false,
      isMaskVisible: false,
      isVisible: this.visible,
      oldPushElMargin: 0,
      oldPushElPlacement: this.placement
    };
  },

  computed: {
    drawerId() {
      return `pendo-drawer-${this._uid}`;
    },

    oppositePlacement() {
      return {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }[this.placement];
    },

    drawerSize() {
      if (['small', 'medium', 'large', 'fullscreen'].includes(this.size)) {
        return {
          small: '300px',
          medium: '450px',
          large: '600px',
          fullscreen: '100'
        }[this.size];
      }

      return this.size;
    },

    panelStyles() {
      const styles = {
        transitionDuration: `${this.duration}ms`
      };

      if (this.push) {
        styles[`border-${this.oppositePlacement}`] = '1px solid #DADCE5';
        styles.boxShadow = 'none';
      }

      if (['top', 'bottom'].includes(this.placement)) {
        return _objectSpread({}, styles, {
          height: this.size === 'fullscreen' ? '100vh' : this.drawerSize
        });
      }

      return _objectSpread({}, styles, {
        width: this.size === 'fullscreen' ? '100vw' : this.drawerSize
      });
    },

    showMask() {
      if (this.size === 'fullscreen') {
        return false;
      }

      return this.push ? this.$options.propsData.mask : this.mask;
    },

    pushEl() {
      if (this.push) {
        return getTarget(this.push);
      }

      return false;
    },

    gridStyles() {
      const footer = this.$slots.footer || this.$scopedSlots.footer;
      const aside = this.$slots.aside || this.$scopedSlots.aside;
      let gridTemplateRows;
      let gridTemplateColumns;

      if (footer) {
        gridTemplateRows = '[header] 68px [body] auto [footer] minmax(68px, max-content)';
      } else {
        gridTemplateRows = '[header] 68px [body] 1fr';
      }

      if (aside) {
        gridTemplateColumns = '25% 1fr';
      }

      return {
        gridTemplateAreas: `'header header' '${aside ? 'aside body' : 'body body'}' 'footer footer'`,
        gridTemplateColumns,
        gridTemplateRows,
        gridColumnGap: aside ? '32px' : 0
      };
    }

  },
  watch: {
    push(newValue, oldValue) {
      if (!newValue && oldValue) {
        setStyles(getTarget(oldValue), {
          transitionDelay: '0ms',
          [`margin-${this.oldPushElPlacement}`]: this.oldPushElMargin
        });
        this.oldPushElPlacement = this.placement;
      }
    },

    visible: {
      handler() {
        if (this.visible) {
          requestAnimationFrame(() => {
            this.isVisible = true;
            this.isPanelVisible = true;
            this.isMaskVisible = true;
          });
        } else if (this.isVisible) {
          this.close();
        }
      },

      immediate: true
    }
  },

  beforeDestroy() {
    this.beforeLeave();
  },

  methods: {
    close() {
      let stopClose = false;

      const stop = () => {
        stopClose = true;
      };

      this.$emit('before-close', {
        stop
      });

      if (!stopClose) {
        this.isPanelVisible = false;
        this.isMaskVisible = false;
        this.$emit('close');
        setTimeout(() => {
          this.$emit('update:visible', false);
          this.$emit('closed');
          this.isVisible = false;
        }, this.duration + 150);
      }
    },

    onMaskClick() {
      if (this.mask && this.closeOnMaskClick) {
        this.close();
      }
    },

    beforeEnter() {
      if (this.pushEl) {
        const styles = {
          willChange: `margin-${this.placement}`,
          transitionDuration: `${this.duration}ms`,
          transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
          transitionDelay: '70ms'
        };
        let movement = this.drawerSize;
        const margins = getMargin(this.pushEl);
        const existingMargin = margins[`margin${capitalize(this.placement)}`];

        if (existingMargin) {
          this.oldPushElMargin = existingMargin;
          movement = `calc(${existingMargin} + ${this.drawerSize})`;
        }

        styles[`margin-${this.placement}`] = movement;
        setStyles(this.pushEl, styles);
      }

      if (this.size === 'fullscreen') {
        document.body.classList.add(SCROLL_BLOCK_CLASS);
      }

      this.$emit('open');
    },

    afterEnter() {
      this.$emit('opened');
    },

    beforeLeave() {
      if (this.pushEl) {
        const styles = {
          transitionDelay: '0ms'
        };

        if (this.oldPushElMargin) {
          styles[`margin-${this.placement}`] = this.oldPushElMargin;
        } else {
          this.pushEl.style.removeProperty(`margin-${this.placement}`);
        }

        setStyles(this.pushEl, styles);
      }

      if (this.size === 'fullscreen') {
        document.body.classList.remove(SCROLL_BLOCK_CLASS);
      }
    },

    afterLeave() {
      if (this.pushEl) {
        this.pushEl.style.removeProperty(`border-${this.placement}`);
        this.pushEl.style.removeProperty('transition-duration');
        this.pushEl.style.removeProperty('transition-delay');
        this.pushEl.style.removeProperty('transition-timing-function');
      }
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('portal', {
    attrs: {
      "disabled": !_vm.isVisible,
      "append-to": _vm.appendTo
    }
  }, [_c('div', {
    class: [_vm.className, 'pendo-drawer', "pendo-drawer--" + _vm.size, "pendo-drawer--" + _vm.placement, {
      'pendo-drawer--push': _vm.push,
      'is-open': _vm.isVisible
    }],
    style: {
      zIndex: _vm.zIndex
    },
    attrs: {
      "id": _vm.drawerId
    }
  }, [_c('transition', {
    attrs: {
      "appear": "",
      "name": "fade"
    }
  }, [_vm.showMask && _vm.isMaskVisible ? _c('div', {
    staticClass: "pendo-drawer__mask",
    on: {
      "click": _vm.onMaskClick
    }
  }) : _vm._e()]), _vm._v(" "), _vm.isVisible ? _c('div', {
    staticClass: "pendo-drawer__wrapper"
  }, [_c('transition', {
    attrs: {
      "appear": "",
      "name": "pendo-transition-slide-" + _vm.oppositePlacement
    },
    on: {
      "before-enter": _vm.beforeEnter,
      "after-enter": _vm.afterEnter,
      "before-leave": _vm.beforeLeave,
      "after-leave": _vm.afterLeave
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.isPanelVisible,
      expression: "isPanelVisible"
    }],
    staticClass: "pendo-drawer__panel",
    class: {
      'pendo-drawer__panel--push': _vm.push
    },
    style: _vm.panelStyles
  }, [_c('div', {
    staticClass: "pendo-drawer__content",
    style: _vm.gridStyles
  }, [_c('div', {
    staticClass: "pendo-drawer__header",
    class: {
      'pendo-drawer__header--fullscreen': _vm.size === 'fullscreen' && (_vm.$slots.header || _vm.$scopedSlots.header || _vm.title)
    }
  }, [_vm._t("header", [_vm.title ? _c('h3', {
    staticClass: "pendo-drawer__title"
  }, [_vm._v("\n                                    " + _vm._s(_vm.title) + "\n                                ")]) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "pendo-drawer__close"
  }, [_c('pendo-button', {
    attrs: {
      "theme": "app",
      "type": "tertiary",
      "icon": "x",
      "icon-size": "20"
    },
    on: {
      "click": _vm.close
    }
  })], 1)], {
    "close": _vm.close
  })], 2), _vm._v(" "), _vm.$slots.aside ? _c('div', {
    staticClass: "pendo-drawer__aside"
  }, [_vm._t("aside")], 2) : _vm._e(), _vm._v(" "), _vm._t("default", [_c('div', {
    staticClass: "pendo-drawer__body"
  }, [_vm._t("body")], 2)]), _vm._v(" "), _vm.$slots.footer || _vm.$scopedSlots.footer ? _c('div', {
    staticClass: "pendo-drawer__footer"
  }, [_vm._t("footer", null, {
    "close": _vm.close
  })], 2) : _vm._e()], 2)])])], 1) : _vm._e()], 1)]);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-drawer.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoDrawer = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoDrawer;
